import React from "react";
import { useDispatch } from "react-redux";
import { logError } from "repoV2/utils/common/error/error";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import { ITimezoneDetails } from "repoV2/features/Common/modules/FormData/interfaces/TimezoneSelector.interfaces";
import { getUserTimezone } from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { DEFAULT_TIMEZONE } from "repoV2/features/Common/modules/Timezone/Timezone.constants";
import { CHECKOUT_PAGES_ACTION_TYPES } from "../constants/CheckoutPagesReducer";
import { callSessionDetailsApi } from "./apis/SessionDetailsApi/SessionDetailsApi";
import { useHideSessionDetailsFormGroup } from "../modules/PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/SessionDetailsFormGroup.utils";
import { ICheckoutPagesAction } from "../interfaces/CheckoutPages.interface";
import { processCallSessionDetailsApiResponse } from "../modules/PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/utils/processCallSessionDetailsApiResponse";

/**
 * @param {boolean} param0.showExpressInterest backend key for `is_express_interest`.
 * @returns
 */
const useFetchNSetCheckoutPageSessionsSection = (props: {
    hostName: string;
    listingUuid: string;
    listingSlug: string;
    listingType?: number;
    listingStatus?: number;
    showExpressInterest?: boolean;
    showExpressInterestFormInitalValue?: boolean;
}) => {
    const {
        hostName,
        listingUuid,
        listingSlug,
        /* start */
        // @dev assuming default placeholder Value for these as Number.POSITIVE_INFINITY
        // (as its the maximum bigNumber available)
        // these values should always be filled but if not then this
        // will be used, as these values (listingType) cannot be bigger than this value
        // and thus this will be ignored in every switch selectors based on these values (listingType),
        listingType = Number.POSITIVE_INFINITY,
        listingStatus = Number.POSITIVE_INFINITY,
        showExpressInterest,
        showExpressInterestFormInitalValue,
        /* start */
    } = props;

    // This state represents if we have to show express interest form to user or not.
    const [showExpressInterestForm, setShowExpressInterestForm] =
        React.useState(showExpressInterestFormInitalValue);

    const dispatch = useDispatch();
    const { hideSessionDetailsFormGroup } = useHideSessionDetailsFormGroup({
        listingType,
        listingStatus,
    });
    const currTimezoneName = useGetFormData<ITimezoneDetails>({
        key: `${FORM_KEYS.timezone_details}.name`,
    });

    const getCheckoutPageSessionDetails = async () => {
        try {
            const data =
                (await callSessionDetailsApi({
                    hostName,
                    listingSlug,
                })) || {};

            const {
                showExpressInterestForm: showExpressInterestFormNewValue,
                sessionDetails,
            } = processCallSessionDetailsApiResponse({
                data,
                listingType,
                showExpressInterest,
            });

            if (showExpressInterestFormNewValue !== showExpressInterestForm)
                setShowExpressInterestForm(showExpressInterestFormNewValue);

            const dispatchArg: ICheckoutPagesAction = {
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_SESSIONS_DATA,
                payload: {
                    data: {
                        ...sessionDetails,
                        startDate: data.start_date,
                        endDate: data.end_date,
                    },
                    isFetched: true,
                    listingUuid,
                },
            };
            dispatch(dispatchArg);
        } catch (error) {
            logError({
                error,
                occuredAt:
                    "repoV2/features/CheckoutPages/utils/useCheckoutPageSessionsData.ts",
                when: "when getCheckoutPageSessionDetails function is called in session api call",
            });
        }
    };

    /**
     * need to refetch the session details on mount
     * if the user is not having the default timezone
     */
    React.useEffect(() => {
        // If we are hiding session details form group, we dont need to call the api at all.
        if (hideSessionDetailsFormGroup) {
            // To keep isFetched state in reducer, to remove loading state
            const dispatchArg: ICheckoutPagesAction = {
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_SESSIONS_DATA,
                payload: {
                    data: {
                        slots: [],
                        classes: [],
                    },
                    isFetched: true,
                    listingUuid,
                },
            };
            dispatch(dispatchArg);
            return;
        }

        // if: this is the initial value of the form (undefined), and the timezone is DEFAULT_TIMEZONE
        // then: we dont need to call the api, as its alread called on the server side
        const userTimezone = getUserTimezone();
        if (!currTimezoneName && userTimezone === DEFAULT_TIMEZONE) return;

        getCheckoutPageSessionDetails();
    }, []);
    /**
     * need to refetch the session details on the change of timezone value (redux) on checkout page
     * as Session Details api returns different session responses
     * (start_time and end_time etc. with timezone offsets)
     * based on this timezone value.
     */
    React.useEffect(() => {
        // We dont need to call the api for initial value. Since its already handled in the above useEffect
        if (!currTimezoneName) return;

        // If we are hiding session details form group, we dont need to call the api at all.
        if (hideSessionDetailsFormGroup) return;

        getCheckoutPageSessionDetails();
    }, [currTimezoneName]);

    return {
        // This 'showExpressInterestForm', is the actual check of whether to show 'express interest' form to
        // the customer or not.
        showExpressInterestForm,
    };
};

export default useFetchNSetCheckoutPageSessionsSection;
